import { useEffect } from "react";
import Diamonds from "images/diamonds.svg";
import PropTypes from "prop-types";
import { displayModal } from "actions/shared-actions";
import { useDispatch, useSelector } from "react-redux";
import FitInformationModal from "components/source/new_taxonomy/fit-information-modal.jsx";
import ActionLogger from "action-logger";
import { getMembershipState, isSubscriptionMember } from "helpers/membership-helpers";
import { userDataPropType } from "components/propTypes";
import FitRecHelpers from "helpers/fit-rec-helpers";

const RecommendedFitTag = ({ fitRates, userData = {}, hideGoodFit = false, clickable = true, className = "" }) => {
  useEffect(() => {
    if (!fitRates || !Array.isArray(fitRates) || !isSubscriptionMember(userData)) {
      return;
    }

    // currently we only disable clicking the tag and hide good fit tags on the grid
    // so this pixel will only fire when a user is viewing the tag on th PDP or Product Drawer - AS 5/22
    const isOnPDPOrProductDrawer = !hideGoodFit && clickable;
    if (isOnPDPOrProductDrawer && recommended) {
      // this pixel for "view" should only fire once after the component mounts, we make sure of that by passing an array as the second argument
      // https://reactjs.org/docs/hooks-effect.html#tip-optimizing-performance-by-skipping-effects
      ActionLogger.logAction({
        ...userFitAnalytics(),
        action: "view",
      });
    }
  }, []);

  const dispatch = useDispatch();
  const membershipState = useSelector(getMembershipState);

  if (!fitRates || !Array.isArray(fitRates)) return null;
  if (!isSubscriptionMember(userData)) return null; // non sub

  const { profiles: { primarySize } = {} } = userData?.userProfile || {};

  const recommended = FitRecHelpers.recommendedFitType(fitRates, primarySize);

  if (!recommended) return null;
  if (hideGoodFit && recommended === "Good") return null;

  const userFitAnalytics = () => {
    const fit = recommended === "Great" ? "great_fit" : "good_fit";
    return {
      object_type: "fit_tag",
      fit: fit,
      user_id: userData.userProfile.id,
      membership_id: membershipState?.membershipId,
    };
  };

  const handleClick = () => {
    ActionLogger.logAction({
      ...userFitAnalytics(),
      action: "click",
    });
    dispatch(displayModal(FitInformationModal.MODAL_NAME));
  };

  const classNames = `text-wine text-sm flex items-center gap-1 ${className}`;
  const tag = (
    <div data-test-id="fit-tag" className={classNames}>
      <Diamonds />
      <span className="whitespace-nowrap">{recommended} Fit</span>
    </div>
  );

  return clickable ? <button onClick={handleClick}>{tag}</button> : tag;
};

RecommendedFitTag.propTypes = {
  fitRates: PropTypes.arrayOf(PropTypes.object),
  userData: userDataPropType,
  hideGoodFit: PropTypes.bool,
  className: PropTypes.string,
  clickable: PropTypes.bool,
};

export default RecommendedFitTag;
